import {
  type ClassificationLevel,
  openModeOf,
  type ProjectDocument,
  type ProjectView,
  type Reader,
  SPEND_LIMIT_REASON,
  type StoredReader,
} from "@esi/shared";
import { canChange } from "./capabilities.ts";

/**
 * Which actions a file row offers, and whether an access change widens who can read it. The
 * server stays the authority for both: these only decide what to show and when to confirm.
 */
export type FileAction = "open" | "download" | "editAccess" | "retry" | "delete" | "remove";

/** Paused by the tenant's hard spend limit: resumes on its own, so it offers no retry. */
export function isPaused(
  document: Pick<ProjectDocument, "status"> & Partial<Pick<ProjectDocument, "failureReason">>,
): boolean {
  return document.status === "failed" && document.failureReason === SPEND_LIMIT_REASON;
}

/**
 * The actions `viewerId` may take on `document`: open (a ready PDF, image, text, Markdown or SVG
 * file, openspec change `open-in-browser`) and download when its classification allows it;
 * edit access, retry (failed, not paused) and delete with `file.manage_any`, or with `file.manage_own` on a
 * file they uploaded, never in a frozen project; `remove` is the confirmation-free removal of an
 * incomplete upload.
 */
export function fileActions(
  document: Pick<ProjectDocument, "status" | "uploadedBy"> &
    Partial<Pick<ProjectDocument, "failureReason" | "mediaType">>,
  project: Pick<ProjectView, "capabilities" | "frozenAt">,
  viewerId: string,
  level: Pick<ClassificationLevel, "downloadAllowed"> | undefined,
): FileAction[] {
  const mine = document.uploadedBy.toLowerCase() === viewerId.toLowerCase();
  const manage =
    canChange(project, "file.manage_any") || (mine && canChange(project, "file.manage_own"));
  const uploading = document.status === "uploading";
  const out: FileAction[] = [];
  if (
    document.status === "ready" &&
    level?.downloadAllowed &&
    openModeOf(document.mediaType ?? "") !== null
  ) {
    out.push("open");
  }
  if (!uploading && level?.downloadAllowed) {
    out.push("download");
  }
  if (manage && uploading) {
    out.push("remove");
  }
  if (manage && !uploading) {
    out.push("editAccess");
  }
  if (manage && document.status === "failed" && !isPaused(document)) {
    out.push("retry");
  }
  if (manage && !uploading) {
    out.push("delete");
  }
  return out;
}

export interface AccessValue {
  readers: readonly (Reader | StoredReader)[];
  classification: string;
  origin: ProjectDocument["origin"];
}

export interface AccessDiff {
  /** Readers the change adds (by kind and reference). */
  added: Reader[];
  /** The new level allows downloading where the old one did not. */
  downloadUnlocked: boolean;
  /** The new level allows a wider kind of reader than the old one. */
  floorWidened: boolean;
  /** Any of the above: the change needs the widening confirmation. */
  widens: boolean;
}

const FLOOR_RANK: Record<ClassificationLevel["readersFloor"], number> = {
  project: 0,
  entity: 1,
  named: 2,
};

const readerKey = (r: Reader | StoredReader) => `${r.kind}:${r.ref.toLowerCase()}`;

/**
 * Whether `before` already lets `reader`'s people in: every reader must be able to read the
 * project, so the project audience covers any reader, and an entity tree covers its root.
 */
function covered(before: readonly (Reader | StoredReader)[], reader: Reader | StoredReader) {
  return before.some(
    (b) =>
      b.kind === "project" ||
      (b.kind === "entity_tree" &&
        reader.kind === "entity" &&
        b.ref.toLowerCase() === reader.ref.toLowerCase()),
  );
}

/**
 * Like the server's added-reader rule (a reader kind and reference not there before), except
 * that a reader the previous audience already covered (named people within the project
 * audience) is a narrowing, not a widening.
 */
export function diffAccess(
  before: AccessValue,
  after: AccessValue,
  scheme: readonly ClassificationLevel[],
): AccessDiff {
  const had = new Set(before.readers.map(readerKey));
  const added = after.readers
    .filter((r) => !had.has(readerKey(r)) && !covered(before.readers, r))
    .map((r) => ({ kind: r.kind, ref: r.ref }) as Reader);
  const from = scheme.find((l) => l.id === before.classification);
  const to = scheme.find((l) => l.id === after.classification);
  const downloadUnlocked = !!from && !!to && !from.downloadAllowed && to.downloadAllowed;
  const floorWidened =
    !!from && !!to && FLOOR_RANK[to.readersFloor] < FLOOR_RANK[from.readersFloor];
  return {
    added,
    downloadUnlocked,
    floorWidened,
    widens: added.length > 0 || downloadUnlocked || floorWidened,
  };
}

/** Whether two reader lists name the same readers (order and case aside). */
export function sameReaders(
  a: readonly (Reader | StoredReader)[],
  b: readonly (Reader | StoredReader)[],
): boolean {
  const left = new Set(a.map(readerKey));
  const right = new Set(b.map(readerKey));
  return left.size === right.size && [...left].every((k) => right.has(k));
}

export { isGeneratedDocument } from "@esi/shared";
